import { createApiClient, apiClient } from "../../api/apiClient";
import { setLoading, setError } from "../../store/slices/shiftHandOverSlice";
import { handleShiftHandoverErrorWithFriendlyMessage, SHIFTHANDOVER_API_NAMES } from "../../store/utils/shiftHandoverErrorHandler";

interface FetchShiftHandoverDataParams {
  setIsLoading: (isLoading: boolean) => void;
  setApiError: (error: string | null) => void;
  dispatch: any;
  selectedLocation?: {
    id: string;
    name?: string;
    displayText?: string;
  } | null;
  selectedFilters?: {
    shift?: string;
    Created_By?: string;
    Modified_By?: string;
    status?: string;
    period?: string;
    week?: string;
    year?: string;
  };
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
}

export interface ShiftHandoverRecord {
  ID: number;
  Attendees: string;
  Title: string;
  Notes: string;
  Location_Name: string;
  Walk_Type: string;
  Created: string;
  Period?: number;
  Week?: number;
  PeriodWeek?: string; // Combined period/week field
  Shift?: string;
  Walk_Number: number;
  Modified: string;
  Created_By: string;
  Modified_By: string | null;
  status: string;
}

export const fetchShiftHandoverData = async ({
  setIsLoading,
  setApiError,
  dispatch,
  selectedLocation,
  selectedFilters = {},
  page = 1,
  pageSize = 100, // Show more records by default
  sortBy = "Created",
  sortOrder = "desc", // Most recent first
}: FetchShiftHandoverDataParams): Promise<ShiftHandoverRecord[]> => {
  try {
    setIsLoading(true);
    dispatch(setLoading(true));
    setApiError(null);

    if (!selectedLocation) {
      throw new Error("No location selected");
    }

    // Get location name, handling both formats (appData and uiPreferences)
    const locationName = selectedLocation.name || selectedLocation.displayText;
    if (!locationName) {
      throw new Error("Invalid location format");
    }

    // Build query parameters
    const params = new URLSearchParams();
    params.append("location", locationName);
    params.append("walkType", "Shift Handover");

    // Add filters if they are specified and not empty
    if (
      selectedFilters.shift &&
      selectedFilters.shift !== "" &&
      selectedFilters.shift !== "All Shifts"
    ) {
      params.append("shift", selectedFilters.shift);
    }
    if (selectedFilters.Created_By && selectedFilters.Created_By !== "") {
      params.append("createdBy", selectedFilters.Created_By);
    }
    if (selectedFilters.Modified_By && selectedFilters.Modified_By !== "") {
      params.append("modifiedBy", selectedFilters.Modified_By);
    }
    if (selectedFilters.status && selectedFilters.status !== "") {
      params.append("status", selectedFilters.status);
    }
    if (selectedFilters.period && selectedFilters.period !== "") {
      params.append("period", selectedFilters.period);
    }
    if (selectedFilters.week && selectedFilters.week !== "") {
      params.append("week", selectedFilters.week);
    }
    if (selectedFilters.year && selectedFilters.year !== "") {
      params.append("year", selectedFilters.year);
    }

    // Add pagination and sorting parameters
    params.append("page", page.toString());
    params.append("pageSize", pageSize.toString());
    params.append("sortBy", sortBy);
    params.append("sortOrder", sortOrder);

    console.log("API request parameters:", Object.fromEntries(params));

    // Make API call
    const api = createApiClient();
    const response = await apiClient(
      api,
      `/shift-handover-walks?${params.toString()}`,
      "GET"
    );

    // Process response data
    if (response && Array.isArray(response)) {
      return response as ShiftHandoverRecord[];
    } else {
      // API returned success but no valid data - treat as error
      console.error("API returned success but invalid data format");
      const userFacingError = "Unable to load shift handovers. Please try again.";
      
      handleShiftHandoverErrorWithFriendlyMessage(
        SHIFTHANDOVER_API_NAMES.FETCH_SHIFT_HANDOVERS,
        userFacingError,
        true, // Show modal overlay
        {
          location: locationName,
          filters: selectedFilters,
          page,
          pageSize,
          reason: "Invalid response format"
        }
      );
      
      // Throw the user-facing error so it is handled by the catch block and the function returns []
      throw new Error(userFacingError);
    }
  } catch (error: any) {
    const baseErrorMessage =
      error.response?.data?.message ||
      error.message ||
      "An error occurred while fetching data";
    // Ensure the message shown to users includes a polite retry suggestion
    const errorMessage = baseErrorMessage.includes("Please try again")
      ? baseErrorMessage
      : `${baseErrorMessage}. Please try again.`;
    console.error("Error fetching shift handover data:", errorMessage);
    
    // Show error overlay modal with the user-facing message
    handleShiftHandoverErrorWithFriendlyMessage(
      SHIFTHANDOVER_API_NAMES.FETCH_SHIFT_HANDOVERS,
      errorMessage,
      true, // Show modal overlay
      {
        location: selectedLocation?.name || selectedLocation?.displayText,
        filters: selectedFilters,
        page,
        pageSize,
        sortBy,
        sortOrder,
        originalError: error,
      }
    );
    
    setApiError(errorMessage);
    dispatch(setError(errorMessage));
    return [];
  } finally {
    setIsLoading(false);
    dispatch(setLoading(false));
  }
};
